import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Clock, 
  Users, 
  Briefcase,
  Target,
  Lightbulb,
  TrendingUp,
  Layers,
  CheckCircle2,
  ArrowUpRight,
  Sparkles,
  Zap
} from 'lucide-react';
import { Container } from '../components/layout/container';
import { getProjectBySlug, getProjectsData } from '../data/projectsData';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import { trackProjectView } from '../utils/analytics';

const CaseStudyPage = () => {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const project = getProjectBySlug(slug, language);
  
  // Track project view
  useEffect(() => {
    if (project) {
      trackProjectView(project.title);
    }
  }, [project]);
  
  // Get adjacent projects for navigation
  const allProjects = getProjectsData(language);
  const currentIndex = allProjects.findIndex(p => p.slug === slug);
  const nextProject = allProjects[currentIndex + 1];
  const prevProject = allProjects[currentIndex - 1];

  if (!project) {
    return (
      <div className="min-h-screen bg-[#0B0B0F]">
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-red-500/5 rounded-full blur-[120px]" />
        </div>
        <div className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#0B0B0F]/60 backdrop-blur-2xl">
          <Container>
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="group flex items-center gap-2 text-white/60 hover:text-white transition-colors duration-150">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 group-hover:bg-white/10 transition-colors">
                  <ArrowLeft className="h-4 w-4" />
                </span>
                <span className="text-sm font-medium">{t.caseStudy?.back || 'Back'}</span>
              </Link>
            </div>
          </Container>
        </div>
        <section className="pt-32 pb-16">
          <Container>
            <div className="text-center max-w-md mx-auto">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/5 flex items-center justify-center">
                <Sparkles className="h-10 w-10 text-white/20" />
              </div>
              <h1 className="text-2xl font-semibold text-white mb-3">
                {t.caseStudy?.notFoundTitle || 'Case Study Not Found'}
              </h1>
              <p className="text-white/50 mb-8">
                {t.caseStudy?.notFoundDesc || "The project you're looking for doesn't exist."}
              </p>
              <Link
                to="/#projects"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0B0B0F] font-medium rounded-xl hover:bg-white/90 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                {t.caseStudy?.viewAllProjects || 'View All Projects'}
              </Link>
            </div>
          </Container>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0F] overflow-hidden">
      <SEO 
        title={`${project.title} - Case Study | Nandan Priyadarshi`}
        description={project.summary}
        canonicalUrl={`/case-study/${project.slug}`}
        keywords={`${project.title}, ${project.tags.join(', ')}, Product Management Case Study, Nandan Priyadarshi`}
        ogType="article"
      />
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-slate-500/8 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/3 right-1/4 w-[600px] h-[600px] bg-indigo-500/5 rounded-full blur-[120px]" />
      </div>

      {/* Navigation */}

      {/* Hero Section */}
      <section className="relative pt-12 pb-16">
        <Container>
          <div className="max-w-5xl mx-auto">
            {/* Category Badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Sparkles className="h-4 w-4 text-slate-400" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">{project.category}</span>
            </motion.div>

            {/* Title */}
            <motion.h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {project.title}
            </motion.h1>

            {/* Tagline */}
            <motion.p
              className="text-xl sm:text-2xl text-white/50 mb-8 max-w-3xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {project.summary}
            </motion.p>

            {/* Meta Info */}
            <motion.div
              className="flex flex-wrap gap-4 mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10">
                <Clock className="h-4 w-4 text-slate-400" />
                <span className="text-sm text-white/70">{project.duration}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10">
                <Briefcase className="h-4 w-4 text-slate-400" />
                <span className="text-sm text-white/70">{project.role}</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10">
                <Users className="h-4 w-4 text-slate-400" />
                <span className="text-sm text-white/70">{project.team}</span>
              </div>
            </motion.div>

            {/* Tags */}
            <motion.div
              className="flex flex-wrap gap-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {project.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1.5 text-sm font-medium bg-white/[0.03] text-white/50 rounded-lg border border-white/[0.08]"
                >
                  {tag}
                </span>
              ))}
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Impact Metrics Banner */}
      <section className="py-12 border-y border-white/5 bg-gradient-to-r from-white/[0.02] via-white/[0.04] to-white/[0.02]">
        <Container>
          <motion.div
            className="max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {project.impact.metrics.map((metric, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">
                    {metric.value}
                  </div>
                  <div className="text-sm text-white/50">{metric.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Problem Section */}
      <section className="py-20">
        <Container>
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-red-500/20 flex items-center justify-center">
                  <Target className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white">{project.problem.title}</h2>
                  <p className="text-sm text-white/40">{t.caseStudy?.understandingPain || 'Understanding the pain'}</p>
                </div>
              </div>

              <p className="text-lg text-white/60 mb-10 max-w-3xl leading-relaxed">
                {project.problem.description}
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {project.problem.painPoints.map((point, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-3 p-5 rounded-2xl bg-red-500/5 border border-red-500/10"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-red-400">{index + 1}</span>
                    </div>
                    <span className="text-white/60">{point}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Approach Section */}
      <section className="py-20 bg-white/[0.01]">
        <Container>
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-indigo-500/20 flex items-center justify-center">
                  <Lightbulb className="h-6 w-6 text-indigo-400" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white">{project.approach.title}</h2>
                  <p className="text-sm text-white/40">{t.caseStudy?.howWeSolvedIt || 'How we solved it'}</p>
                </div>
              </div>

              <p className="text-lg text-white/60 mb-12 max-w-3xl leading-relaxed">
                {project.approach.description}
              </p>

              {/* Timeline */}
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-indigo-500/50 via-indigo-500/20 to-transparent hidden md:block" />

                <div className="space-y-8">
                  {project.approach.phases.map((phase, phaseIndex) => (
                    <motion.div
                      key={phaseIndex}
                      className="relative md:pl-16"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: phaseIndex * 0.15 }}
                    >
                      {/* Timeline dot */}
                      <div className="absolute left-0 top-0 w-12 h-12 rounded-2xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center hidden md:flex">
                        <span className="text-lg font-bold text-indigo-400">{phaseIndex + 1}</span>
                      </div>

                      <div className="rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-transparent p-6 md:p-8">
                        <div className="flex flex-wrap items-center gap-4 mb-4">
                          <h3 className="text-xl font-semibold text-white">{phase.name}</h3>
                          <span className="px-3 py-1 text-xs font-medium bg-indigo-500/20 text-indigo-300 rounded-full">
                            {phase.duration}
                          </span>
                        </div>

                        <div className="space-y-3">
                          {phase.activities.map((activity, actIndex) => (
                            <div key={actIndex} className="flex items-start gap-3">
                              <CheckCircle2 className="h-5 w-5 text-indigo-400 shrink-0 mt-0.5" />
                              <span className="text-white/60">{activity}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Impact Section */}
      <section className="py-20">
        <Container>
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white">{project.impact.title}</h2>
                  <p className="text-sm text-white/40">{t.caseStudy?.resultsDelivered || 'Results delivered'}</p>
                </div>
              </div>

              <p className="text-lg text-white/60 mb-10 max-w-3xl leading-relaxed">
                {project.impact.description}
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {project.impact.outcomes.map((outcome, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-3 p-5 rounded-2xl bg-emerald-500/5 border border-emerald-500/10"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Zap className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" />
                    <span className="text-white/60">{outcome}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Tech Stack & Learnings */}
      <section className="py-20 bg-white/[0.01]">
        <Container>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Tech Stack */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-slate-500/20 flex items-center justify-center">
                    <Layers className="h-5 w-5 text-slate-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{t.caseStudy?.techStack || 'Tech Stack'}</h3>
                </div>

                <div className="flex flex-wrap gap-2">
                  {project.techStack.map((tech, index) => (
                    <motion.span
                      key={index}
                      className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-slate-500/20 to-slate-600/10 text-slate-200 rounded-xl border border-slate-500/20"
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ scale: 1.05 }}
                    >
                      {tech}
                    </motion.span>
                  ))}
                </div>
              </motion.div>

              {/* Key Learnings */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <Lightbulb className="h-5 w-5 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{t.caseStudy?.keyLearnings || 'Key Learnings'}</h3>
                </div>

                <div className="space-y-3">
                  {project.learnings.map((learning, index) => (
                    <motion.div
                      key={index}
                      className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/5 border border-amber-500/10"
                      initial={{ opacity: 0, x: 10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <span className="text-amber-400 font-bold">{index + 1}.</span>
                      <span className="text-white/60 text-sm">{learning}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </Container>
      </section>

      {/* Navigation Footer */}
      <section className="py-12 border-t border-white/5">
        <Container>
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row items-stretch gap-4">
              {prevProject && (
                <Link
                  to={`/case-study/${prevProject.slug}`}
                  className="group flex-1 p-6 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 transition-[background-color,border-color] duration-200"
                >
                  <div className="flex items-center gap-2 text-sm text-white/40 mb-2">
                    <ArrowLeft className="h-4 w-4" />
                    {t.caseStudy?.previousCaseStudy || 'Previous Case Study'}
                  </div>
                  <div className="text-lg font-semibold text-white group-hover:text-white/90 transition-colors">
                    {prevProject.title}
                  </div>
                </Link>
              )}
              
              {nextProject && (
                <Link
                  to={`/case-study/${nextProject.slug}`}
                  className="group flex-1 p-6 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 transition-[background-color,border-color] duration-200 text-right"
                >
                  <div className="flex items-center justify-end gap-2 text-sm text-white/40 mb-2">
                    {t.caseStudy?.nextCaseStudy || 'Next Case Study'}
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                  <div className="text-lg font-semibold text-white group-hover:text-white/90 transition-colors">
                    {nextProject.title}
                  </div>
                </Link>
              )}
            </div>

            {/* CTA */}
            <motion.div
              className="mt-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-white/50 mb-4">{t.caseStudy?.interestedWorking || 'Interested in working together?'}</p>
              <Link
                to="/contact-me"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0B0B0F] font-medium rounded-xl hover:bg-white/90 transition-colors"
              >
                {t.caseStudy?.letsTalk || "Let's Talk"}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default CaseStudyPage;
